import React from "react";
import { Flex, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import {
  DatabaseOutlined,
  SettingOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import SbCard from "uiCore/SbCard/SbCard";
import { CardTagStyled } from "Pages/AdminSetup/AdminSetup.styles";
import useEntitlementsStore from "Store/useEntitlementsStore";
import { useHasAccess } from "Routes/hooks/useHasAccess";
import {
  PageWrapperStyled,
  SectionWrapperStyled,
  SectionHeaderStyled,
  SectionTitleStyled,
  CardGridStyled,
  CardIconWrapperStyled,
} from "./SystemAdminSetup.styles";

const { Title, Text } = Typography;

const SYSTEM_ADMIN_CARDS = [
  {
    title: "Org Registry Management",
    desc: "Manage organization context library",
    icon: <DatabaseOutlined />,
    badge: "Manage",
    badgeColor: "success",
    badgeIcon: <CheckCircleFilled />,
    route: "/system-admin-setup/org-registry-management",
    resource: "system_admin_setup-org_registry_management",
    actions: ["view"],
  },
];

// Reusable card component
const ModuleCard = ({ card, onNavigate }) => (
  <SbCard
    hoverable
    size="small"
    onClick={() => card.route && onNavigate(card.route)}
    styles={{ body: { padding: "18px" } }}
  >
    <CardIconWrapperStyled align="center" justify="center">
      {card.icon}
    </CardIconWrapperStyled>

    <Text strong style={{ display: "block", marginBottom: 4 }}>
      {card.title}
    </Text>

    <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
      {card.desc}
    </Text>

    {card.badge && (
      <CardTagStyled icon={card.badgeIcon}>
        {card.badge}
      </CardTagStyled>
    )}
  </SbCard>
);

// Check if user has access to a card based on entitlements
const cardHasAccess = (card, entitlements) => {
  if (!card.resource) return true;
  const match = entitlements?.find((e) => e.name === card.resource);
  const allowed = match?.entitlements ?? [];
  return card.actions?.some((a) => allowed.includes(a) || allowed.includes('*')) ?? false;
};

// Section renderer — only renders if at least one card is accessible
const SectionCards = ({ title, icon, cards, onNavigate, entitlements }) => {
  const visibleCards = cards.filter((card) => cardHasAccess(card, entitlements));
  if (!visibleCards.length) return null;

  return (
    <SectionWrapperStyled>
      <SectionHeaderStyled align="center">
        {icon}
        <SectionTitleStyled>{title}</SectionTitleStyled>
      </SectionHeaderStyled>
      <CardGridStyled>
        {visibleCards.map((card) => (
          <ModuleCard key={card.title} card={card} onNavigate={onNavigate} />
        ))}
      </CardGridStyled>
    </SectionWrapperStyled>
  );
};

const SystemAdminSetup = () => {
  const navigate = useNavigate();
  const { entitlements } = useEntitlementsStore();
  const { hasAccess } = useHasAccess();

  // Gate on the system_admin_setup entitlement (same as the route config), not a hardcoded role name
  const canViewSystemAdminSetup = hasAccess('system_admin_setup', ['view']);

  if (!canViewSystemAdminSetup) {
    return (
      <PageWrapperStyled>
        <Flex align="center" justify="center" style={{ minHeight: '60vh' }}>
          <Typography.Text type="secondary">
            You don't have access to System Admin Setup.
          </Typography.Text>
        </Flex>
      </PageWrapperStyled>
    );
  }

  return (
    <PageWrapperStyled>
      <Flex align="center" gap={8} style={{ marginBottom: 32 }}>
        <Title level={3} style={{ margin: 0 }}>
          System Admin Setup
        </Title>
      </Flex>

      <SectionCards
        title="System Configuration"
        icon={<SettingOutlined />}
        cards={SYSTEM_ADMIN_CARDS}
        onNavigate={navigate}
        entitlements={entitlements}
      />
    </PageWrapperStyled>
  );
};

export default SystemAdminSetup;
